import React, { useEffect, useState } from 'react'

import { useHistory } from 'react-router-dom'

import { useAppInitActions } from 'redux/actions/AppInit'
import { useAuthenticationActions } from 'redux/actions/Authentication'
import {
  useIsAuthenticated,
  useIsValidatingUser
} from 'redux/hooks/SignedInAccount'

import AuthTemplate from 'components_new/templates/AuthTemplate'
import SimpleDialog from 'components_new/molecules/SimpleDialog'

import ActivateAccount from './ActivateAccount'
import ForgotPassword from './ForgotPassword'
import ResetPassword from './ResetPassword'
import SignIn from './SignIn'
import MFAVerify from './MFAVerify'

import { getSession, getUserFromEmail, clearCookies } from './AuthModule'

import { registerSessionExpiredHandler } from 'utils/axios'

import {
  LOGIN,
  FORGOT_PASSWORD,
  VERIFICATION_CODE,
  SET_PASSWORD,
  MFA_REQUIRED,
  SIGNED_IN,
  LOGIN_WITH_AZURE
} from './utils'

import {
  handleIdp,
  handleLogin,
  handleSetPassword,
  handleForgotPassword,
  handleVerificationCode,
  handleSsoLookup,
  handleMFA
} from './ActionHandlers'
import { signOut } from './AuthModule'
import Button from 'components_new/atoms/Button'
import { ThemeProvider } from '@mui/material'
import { getTheme } from 'themes'

const RESTART_LOCATION =
  process.env.REACT_APP_PORTAL_URL ||
  (process.env.NODE_ENV === 'production'
    ? 'https://portal.homepal.se'
    : 'http://localhost:3000')

// API origin used to whitelist return_to URLs after MCP-OAuth login bounce.
// Anything else is rejected as a potential open-redirect.
function getApiOrigin() {
  if (process.env.NODE_ENV !== 'production') {
    return 'http://localhost:3990'
  }

  try {
    return new URL(process.env.REACT_APP_HOMEPAL_MDM_API_PROD).origin
  } catch {
    return 'https://api.homepal.se'
  }
}

const API_ORIGIN = getApiOrigin()

// Survives the Cognito/Entra round trip. The IdP redirect_uri is a fixed
// '/callback' with no room for extra params, so return_to is stripped from
// the URL somewhere between /login and coming back signed in.
const RETURN_TO_KEY = 'homepal.mcp_oauth_return_to'
// Guards against a return_to that keeps bouncing back to /login (e.g. an
// /authorize that cannot resolve the session). Without a ceiling the portal
// and the API would ping-pong forever.
const RETURN_TO_BOUNCES_KEY = 'homepal.mcp_oauth_return_to_bounces'
const MAX_RETURN_TO_BOUNCES = 3

function validateReturnTo(raw) {
  if (!raw) return null

  try {
    const url = new URL(raw)

    if (url.origin !== API_ORIGIN) return null
    if (!url.pathname.startsWith('/authorize')) return null

    return url.toString()
  } catch {
    return null
  }
}

function readSession(key) {
  try {
    return window.sessionStorage.getItem(key)
  } catch {
    return null
  }
}

function writeSession(key, value) {
  try {
    window.sessionStorage.setItem(key, value)
  } catch {
    // Private mode / storage disabled — the in-URL return_to still works for
    // the already-signed-in case, only the IdP round trip degrades.
  }
}

function clearSession(key) {
  try {
    window.sessionStorage.removeItem(key)
  } catch {
    // Nothing to do.
  }
}

/**
 * Resolve the MCP-OAuth return_to for this page load, preferring the query
 * string and falling back to what a previous load stashed. Stashing is what
 * makes SSO work: the API bounces to /login?return_to=..., the user is sent
 * off to Entra ID, and Cognito returns them to '/callback?code=...' with the
 * return_to long gone from the URL.
 */
function captureReturnTo() {
  const fromQuery = validateReturnTo(
    new URLSearchParams(window.location.search).get('return_to')
  )

  if (fromQuery) {
    const previous = readSession(RETURN_TO_KEY)
    const bounces
      = previous === fromQuery
        ? Number(readSession(RETURN_TO_BOUNCES_KEY) || 0)
        : 0

    if (bounces >= MAX_RETURN_TO_BOUNCES) {
      // /authorize sent us back here for the Nth time without ever finishing.
      // Drop the hand-off and leave the user on the portal rather than
      // looping — the OAuth client will report its own timeout.
      clearSession(RETURN_TO_KEY)
      clearSession(RETURN_TO_BOUNCES_KEY)

      return null
    }

    writeSession(RETURN_TO_KEY, fromQuery)
    writeSession(RETURN_TO_BOUNCES_KEY, String(bounces + 1))

    return fromQuery
  }

  return validateReturnTo(readSession(RETURN_TO_KEY))
}

// Captured synchronously at module load, before React Router / RedirectWrapper
// can navigate to '/' and strip the query string. RedirectWrapper is a child
// of <Auth>, so its redirect effect runs BEFORE Auth's own effects on mount —
// reading window.location.search inside an effect would already be too late
// for an already-authenticated user. This stashed value survives that.
const INITIAL_RETURN_TO = captureReturnTo()

let returnToConsumed = false

/**
 * Hand the browser back to the API's /authorize so it can mint a code now
 * that a cookie session exists. Clears the stash first so an abandoned flow
 * cannot hijack a later navigation in the same tab.
 */
function followReturnTo() {
  if (returnToConsumed || !INITIAL_RETURN_TO) return

  returnToConsumed = true
  clearSession(RETURN_TO_KEY)

  window.location.replace(INITIAL_RETURN_TO)
}

const Auth = ({ children }) => {
  const isAuthenticated = useIsAuthenticated()
  const isValidatingUser = useIsValidatingUser()
  const { tryValidateUser, signOut: signOutSession }
    = useAuthenticationActions()
  const { initializeApp } = useAppInitActions()

  const [blank, setBlank] = useState(false)
  const [loading, setLoading] = useState(false)
  const [state, setState] = useState(null)
  const [user, setUser] = useState(null)
  const [email, setEmail] = useState('')
  const [confirmationCode, setConfirmationCode] = useState('')
  const [invitedBy, setInvitedBy] = useState(null)
  const [dashboardName, setDashboardName] = useState(null)
  const [errors, setErrors] = useState([])
  const [idpSelections, setIdpSelections] = useState([])
  const [rememberMe, setRememberMe] = useState(false)
  const history = useHistory()

  // Lets the axios response interceptor sign out on an expired session.
  useEffect(() => {
    registerSessionExpiredHandler(signOutSession)
  }, [signOutSession])

  useEffect(() => {
    setBlank(true)

    if (window.location.search.includes('&confirmation_code=')) {
      const email = window.location.search.split('email=')[1].split('&')[0]
      const code = window.location.search.split('confirmation_code=')[1]

      setConfirmationCode(code)
      setUser(getUserFromEmail(email.toLowerCase()))
      setState(VERIFICATION_CODE)
      setBlank(false)
    } else if (window.location.search.includes('?code=')) {
      // This is triggered twice in React.Strictmode so if testing
      // make sure to deactivate.
      handleIdp(setState, setBlank)
        .then((errors) => {
          if (errors.length > 0) {
            signOut()
              .then(() => {
                history.push('/login')
                setErrors(errors)
              })
              .catch(() => {
                setErrors(errors)
                history.push('/login')
              })
          }
        })
        .catch(() => {
          signOut()
            .then(() => {
              window.location = RESTART_LOCATION
            })
            .catch(() => {
              window.location = RESTART_LOCATION
            })
        })
    } else if (
      window.location.search.includes('email=') &&
      window.location.search.includes('chal=')
    ) {
      const handleCreateAccount = () => {
        const email = window.location.search.split('email=')[1].split('&')[0]

        const chal = window.location.search.split('chal=')[1]
        const redirectOnError = () => {
          history.push('/login')
          setState(LOGIN)
        }

        handleLogin(
          { email: email, password: chal },
          setState,
          setUser,
          false,
          redirectOnError
        )
        setBlank(false)
        setEmail(email)
      }

      signOut()
        .then(() => {
          handleCreateAccount()
        })
        .catch(() => handleCreateAccount())
    } else {
      getSession()
        .then(() => {
          setState(SIGNED_IN)
          setBlank(false)
        })
        .catch(() => {
          clearCookies()
          setState(LOGIN)
          setBlank(false)
        })
    }
  }, [])

  useEffect(() => {
    if (state === SIGNED_IN && !isAuthenticated && !isValidatingUser) {
      tryValidateUser((err) => {
        if (!err) {
          initializeApp()
        }
      })
    }

    // MCP OAuth: if we landed here with a whitelisted return_to (set by
    // the API's /authorize bounce-to-login), kick the browser back there
    // so /authorize can mint a code now that the cookie session exists.
    if (state === SIGNED_IN && INITIAL_RETURN_TO) {
      followReturnTo()
    }
  }, [state])

  // MCP OAuth: the effect above only fires on a fresh sign-in. A user who is
  // ALREADY authenticated when the API's /authorize bounces them here never
  // transitions through SIGNED_IN, so honor return_to on arrival too —
  // otherwise the flow dead-ends on the portal start screen. The redirect
  // target is same-site (api.homepal.se), so the session cookie is sent and
  // /authorize can proceed to consent. INITIAL_RETURN_TO is captured at
  // module load (RedirectWrapper strips the query first), and it whitelists
  // the origin + /authorize path, so this is not an open redirect.
  useEffect(() => {
    if (isAuthenticated && INITIAL_RETURN_TO) {
      followReturnTo()
    }
  }, [isAuthenticated])

  useEffect(() => {
    if (!isAuthenticated && state) {
      setState(LOGIN)
    }
  }, [isAuthenticated])

  useEffect(() => {
    if (
      state &&
      state !== SIGNED_IN &&
      window.location.pathname.includes('dashboards')
    ) {
      const splitSearch = window.location.search.split('&')
      /*
        When inviting a user, the search link and thereby the split should 
        generate the following array:
        [
          ?email={username},
          chal={####},
          invitedBy=${event.request.clientMetaData.invitedBy}
          dashboardName=${event.request.clientMetaData.dashboardName}
        ]
      */
      let paramRef = ''

      splitSearch.forEach((queryParam) => {
        if (queryParam.includes('invitedBy')) {
          setInvitedBy(queryParam.split('=')[1])
        } else if (queryParam.includes('dashboardName')) {
          setDashboardName(queryParam.split('=')[1])
        } else if (queryParam.includes('ref=')) {
          paramRef = queryParam
        }
      })

      const redirectPath = window.location.pathname + paramRef

      history.push('/login', { redirectPath })
    } else if (
      state &&
      state !== SIGNED_IN &&
      window.location.pathname !== '/login'
    ) {
      history.push('/login')
    }
  }, [state])

  if ((blank && state !== SIGNED_IN) || !state) {
    return null
  }

  return state === SIGNED_IN ? (
    <>{children}</>
  ) : (
    <AuthTemplate>
      <ThemeProvider theme={getTheme('light')}>
        <SimpleDialog
          open={idpSelections.length > 1}
          title={'Logga in med'}
          onClose={() => {
            setIdpSelections([])
            setLoading(false)
          }}
          actions={
            <Button
              variant={'text'}
              onClick={() => {
                setIdpSelections([])
                setLoading(false)
              }}
            >
              Stäng
            </Button>
          }
        >
          {idpSelections}
        </SimpleDialog>
      </ThemeProvider>
      {state === LOGIN ? (
        <SignIn
          errors={errors}
          loading={loading}
          onForgetPassword={() => setState(FORGOT_PASSWORD)}
          onSignIn={async (email, password, rememberMeValue, onlyEmail) => {
            setLoading(true)
            setRememberMe(rememberMeValue)
            if (onlyEmail) {
              const { usePassword, errors } = await handleSsoLookup(
                { email },
                setIdpSelections
              )

              setErrors(errors)
              setLoading(false)

              return usePassword
            }

            const errors = await handleLogin(
              { email, password },
              setState,
              setUser,
              rememberMeValue
            )

            setErrors(errors)
            setLoading(false)

            return true
          }}
          onSignInWithMicrosoft={() => setState(LOGIN_WITH_AZURE)}
          setErrors={(errors) => setErrors(errors)}
        />
      ) : null}

      {state === FORGOT_PASSWORD ? (
        <ForgotPassword
          errors={errors}
          loading={loading}
          onBackToSignIn={() => setState(LOGIN)}
          onForgotPassword={async (email, setPasswordHasBeenReset) => {
            setLoading(true)

            const errors = await handleForgotPassword(
              { email },
              setPasswordHasBeenReset
            )

            setErrors(errors)

            setLoading(false)
          }}
          setErrors={(errors) => setErrors(errors)}
        />
      ) : null}

      {state === VERIFICATION_CODE ? (
        <ResetPassword
          confirmationCode={confirmationCode}
          errors={errors}
          loading={false}
          onBackToSignIn={() => setState(LOGIN)}
          onResetPassword={async (code, password) => {
            setLoading(true)

            const errors = await handleVerificationCode(
              { password, code },
              setState,
              user
            )

            setErrors(errors)

            setLoading(false)
          }}
          setErrors={(errors) => setErrors(errors)}
        />
      ) : null}

      {state === SET_PASSWORD ? (
        <ActivateAccount
          dashboardName={dashboardName}
          email={email || user?.username}
          errors={errors}
          invitedBy={invitedBy}
          loading={loading}
          onActivateAccount={async (password, firstName, lastName) => {
            setLoading(true)

            const errors = await handleSetPassword(
              { password, first_name: firstName, last_name: lastName },
              setState,
              user,
              email || user?.username
            )

            setErrors(errors)

            setLoading(false)
          }}
          setErrors={(errors) => setErrors(errors)}
        />
      ) : null}

      {state === MFA_REQUIRED ? (
        <MFAVerify
          errors={errors}
          loading={loading}
          onBackToSignIn={() => setState(LOGIN)}
          onVerifyMFA={async (code) => {
            setLoading(true)

            const errors = await handleMFA({ code }, setState, user, rememberMe)

            setErrors(errors)

            setLoading(false)
          }}
          setErrors={(errors) => setErrors(errors)}
        />
      ) : null}
    </AuthTemplate>
  )
}

export default Auth
